import { clsx } from 'clsx'
import { FoldVertical, PanelRightOpen, UnfoldVertical } from 'lucide-react'
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { IconButton } from '@/components/Button'
import { useBoardCtx } from '@/board/context'
import { CardText } from '@/board/items/CardText'
import { useI18n } from '@/i18n/i18n'
import { type Edge } from '@shared/board'
import { cardTitle } from './describe'
import {
  changedSince,
  type EmbedTarget,
  resolveEmbed,
  type Scene,
  sceneKey,
  stackedCards,
} from './embeds'
import { useFocusMention } from './focus'
import { type Mention } from './mentions'
import { SceneView } from './SceneView'
import { useOpenTurnPanel, useTurnId } from './turnPanel'

// Part of the board inside what the AI said (DESIGN.md "UI"): a card as a
// compact card, a group or region as a small scene, media as on the board.
// Read-only; it can be peeked (expanded in place) or opened (the canvas comes
// into view and moves there), and it says when it changed after its turn.

/** How tall an embed is until peeked, and at most once peeked. */
const FOLDED = 220
const PEEKED = 560

/** A lone thing that is not a card shows as itself, at its board size or smaller. */
function soloScene(target: Extract<EmbedTarget, { kind: 'item' }>): Scene {
  const { item } = target
  return {
    items: [item],
    edges: [],
    frame: { x: item.x, y: item.y, w: item.w, h: item.h },
    missing: [],
    nearby: [],
  }
}

/**
 * Cards, reflowed to the message's width: they are there to be read. One card,
 * or a few stacked in reading order with the connections between them named
 * first (they stay in view while folded).
 */
function CardsEmbed({
  ids,
  links = [],
  peeked,
  whole = false,
  onOverflow,
}: {
  ids: string[]
  links?: Edge[]
  peeked: boolean
  /** Shown in full, never folded: one card the AI put in its reply is the reply. */
  whole?: boolean
  onOverflow: (v: boolean) => void
}) {
  const { client } = useBoardCtx()
  const { t } = useI18n()
  const box = useRef<HTMLDivElement>(null)
  const [clipped, setClipped] = useState(false)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const check = () => {
      const over = !whole && el.scrollHeight > FOLDED + 1
      setClipped(over)
      onOverflow(over)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el.firstElementChild ?? el)
    return () => {
      ro.disconnect()
    }
  }, [onOverflow, whole])
  return (
    <div
      ref={box}
      className={clsx('relative overflow-hidden', !peeked && clipped && 'moot-embed-fade')}
      style={peeked || whole ? undefined : { maxHeight: FOLDED }}
    >
      {links.length > 0 && (
        <ul className="border-b border-line px-4 py-1.5 text-[12px] text-muted">
          {links.map((e) => (
            <li key={e.id} className="truncate">
              {cardTitle(client.doc, e.from) || t('embed.untitled')} →{' '}
              {cardTitle(client.doc, e.to) || t('embed.untitled')}
              {e.label && <span className="text-ink-2"> · {e.label}</span>}
            </li>
          ))}
        </ul>
      )}
      {ids.map((id, i) => (
        <CardText
          key={id}
          id={id}
          className={clsx('px-4 pt-3.5 pb-3.5', i > 0 && 'border-t border-line')}
        />
      ))}
    </div>
  )
}

/** The refs an embed shows (things and their text), to ask what happened to them since. */
function embedRefs(target: EmbedTarget): string[] {
  const items =
    target.kind === 'item' ? [target.item] : target.kind === 'scene' ? target.scene.items : []
  const edges = target.kind === 'scene' ? target.scene.edges : []
  return [
    ...items.flatMap((i) => [`item:${i.id}`, ...(i.kind === 'card' ? [`text:${i.id}`] : [])]),
    ...edges.map((e) => `edge:${e.id}`),
  ]
}

/** The previous scene while it draws the same thing, so the drawing is not redone. */
function useStableScene(next: Scene | null): Scene | null {
  const key = next ? sceneKey(next) : ''
  // Equal keys draw the same picture: keep the first scene with this key.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => next, [key])
}

export function Embed({
  mention,
  caption,
  since,
}: {
  mention: Mention
  /** The image syntax's alt text: what the AI called it. */
  caption: string
  /** When the turn that said it ended (server clock); null while it runs. */
  since: number | null
}) {
  const { client } = useBoardCtx()
  const { t } = useI18n()
  const focus = useFocusMention()
  const openPanel = useOpenTurnPanel()
  const turnId = useTurnId()
  const snap = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const [peeked, setPeeked] = useState(false)
  const [cardOverflows, setCardOverflows] = useState(false)
  // Resolve again only when the board changed or it points elsewhere (the
  // mention object itself is new on every render of the message).
  const key = JSON.stringify(mention)
  const target = useMemo(
    () => resolveEmbed(JSON.parse(key) as Mention, snap.state),
    [key, snap.state],
  )
  const touched = useSyncExternalStore(client.subscribeTouched, client.getTouched)
  const changed = since !== null && changedSince(target, since, touched)
  const open = () => {
    void focus(mention)
  }
  const pick = useCallback(
    (id: string) => {
      void focus({ kind: 'item', id })
    },
    [focus],
  )

  const card = target.kind === 'item' && target.item.kind === 'card'
  const stacked = target.kind === 'scene' ? stackedCards(target.scene) : null
  const scene = useStableScene(
    target.kind === 'scene'
      ? target.scene
      : target.kind === 'item' && !card
        ? soloScene(target)
        : null,
  )

  if (target.kind === 'gone')
    return (
      <span className="my-2 block rounded-lg border border-dashed border-line-strong px-3 py-2.5 text-[12.5px] text-muted">
        {t('embed.gone')}
      </span>
    )
  // Peeking only means something when there is more to see.
  const canPeek = card || stacked ? cardOverflows : scene !== null && scene.frame.h > FOLDED
  // A card shows its own title; anything else is named by what the AI called it.
  const label = card ? '' : caption || (target.kind === 'item' ? '' : t('embed.region'))

  return (
    <figure
      className="moot-embed group/embed relative my-2.5 overflow-hidden rounded-lg border border-line bg-surface"
      aria-label={caption || label || t('embed.label')}
      onDoubleClick={open}
    >
      {card ? (
        <CardsEmbed ids={[target.item.id]} peeked={peeked} whole onOverflow={setCardOverflows} />
      ) : stacked ? (
        <CardsEmbed
          ids={stacked.cards.map((c) => c.id)}
          links={stacked.links}
          peeked={peeked}
          onOverflow={setCardOverflows}
        />
      ) : (
        scene && <SceneView scene={scene} maxHeight={peeked ? PEEKED : FOLDED} onPick={pick} />
      )}
      {target.kind === 'scene' && scene?.items.length === 0 && (
        <span className="absolute inset-0 grid place-items-center text-[12.5px] text-muted">
          {t('embed.empty')}
        </span>
      )}
      {(label || changed) && (
        <figcaption className="flex items-center gap-2 border-t border-line px-3 py-1.5 text-[12px] text-muted">
          {label && <span className="min-w-0 truncate">{label}</span>}
          {changed &&
            (openPanel && turnId ? (
              <button
                type="button"
                onClick={() => {
                  openPanel(turnId, 'changes', embedRefs(target))
                }}
                className="ml-auto flex-none rounded-full bg-me-soft px-2 py-px text-[11px] font-medium text-me hover:brightness-95"
                title={t('embed.changedHint')}
              >
                {t('embed.changed')}
              </button>
            ) : (
              <span
                className="ml-auto flex-none rounded-full bg-me-soft px-2 py-px text-[11px] font-medium text-me"
                title={t('embed.changedHint')}
              >
                {t('embed.changed')}
              </span>
            ))}
        </figcaption>
      )}
      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-md border border-line bg-surface/90 p-0.5 opacity-0 shadow-sm backdrop-blur transition-opacity group-hover/embed:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
        {canPeek && (
          <IconButton
            label={peeked ? t('embed.fold') : t('embed.peek')}
            aria-expanded={peeked}
            className="size-7"
            onClick={() => {
              setPeeked((v) => !v)
            }}
          >
            {peeked ? <FoldVertical size={14} /> : <UnfoldVertical size={14} />}
          </IconButton>
        )}
        <IconButton label={t('embed.open')} className="size-7" onClick={open}>
          <PanelRightOpen size={14} />
        </IconButton>
      </div>
    </figure>
  )
}
